import React, { useState } from 'react';
import { communicationsAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';

const Communications = () => {
  const [emailForm, setEmailForm] = useState({
    to_email: '',
    subject: '',
    message: '',
    lead_id: ''
  });

  const [smsForm, setSmsForm] = useState({
    to_phone: '',
    message: '',
    lead_id: ''
  });

  const [sending, setSending] = useState(false);
  const applicationLink = `${window.location.origin}/apply`;

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await communicationsAPI.sendEmail(emailForm);
      toast.success('Email sent successfully!');
      setEmailForm({ to_email: '', subject: '', message: '', lead_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleSendSMS = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await communicationsAPI.sendSMS(smsForm);
      toast.success('SMS sent successfully!');
      setSmsForm({ to_phone: '', message: '', lead_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const sendApplicationLink = (via) => {
    const message = `Please complete your loan application using this link: ${applicationLink}`;
    
    if (via === 'email') {
      setEmailForm({
        ...emailForm,
        subject: 'Complete Your Loan Application',
        message: message
      });
    } else {
      setSmsForm({
        ...smsForm,
        message: message
      });
    }
    toast.success(`Application link inserted into ${via} form`);
  };

  const copyApplicationLink = () => {
    navigator.clipboard.writeText(applicationLink);
    toast.success('Application link copied to clipboard!');
  };

  return (
    <div className="p-6 space-y-6" data-testid="communications-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-primary">Communications</h1>
        <p className="text-muted-foreground mt-2">Send emails and SMS to your leads</p>
      </div>

      {/* Application Link Section */}
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader>
          <CardTitle>Application Link</CardTitle>
          <CardDescription>Share this link with leads to collect their loan application</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Input value={applicationLink} readOnly className="font-mono text-sm" />
            <Button onClick={copyApplicationLink} variant="outline">
              Copy Link
            </Button>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => sendApplicationLink('email')} variant="secondary" size="sm">
              Insert into Email
            </Button>
            <Button onClick={() => sendApplicationLink('sms')} variant="secondary" size="sm">
              Insert into SMS
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="email" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="email" data-testid="email-tab">
            <Mail className="h-4 w-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="sms" data-testid="sms-tab">
            <MessageSquare className="h-4 w-4 mr-2" />
            SMS
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle>Send Email</CardTitle>
              <CardDescription>Compose and send an email to a lead or lender</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email_to">Recipient Email *</Label>
                  <Input
                    id="email_to"
                    type="email"
                    value={emailForm.to_email}
                    onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
                    placeholder="recipient@example.com"
                    required
                    data-testid="email-to-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email_subject">Subject *</Label>
                  <Input
                    id="email_subject"
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                    placeholder="Enter subject"
                    required
                    data-testid="email-subject-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email_message">Message *</Label>
                  <Textarea
                    id="email_message"
                    value={emailForm.message}
                    onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                    placeholder="Enter your message"
                    rows={6}
                    required
                    data-testid="email-message-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email_lead_id">Lead ID (optional)</Label>
                  <Input
                    id="email_lead_id"
                    value={emailForm.lead_id}
                    onChange={(e) => setEmailForm({ ...emailForm, lead_id: e.target.value })}
                    placeholder="lead_abc123"
                    data-testid="email-lead-id-input"
                  />
                </div>

                <Button type="submit" className="w-full gap-2" disabled={sending} data-testid="send-email-button">
                  <Send className="h-4 w-4" />
                  {sending ? 'Sending...' : 'Send Email'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sms">
          <Card>
            <CardHeader>
              <CardTitle>Send SMS</CardTitle>
              <CardDescription>Send a text message to a lead</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendSMS} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sms_phone">Phone Number *</Label>
                  <Input
                    id="sms_phone"
                    type="tel"
                    value={smsForm.to_phone}
                    onChange={(e) => setSmsForm({ ...smsForm, to_phone: e.target.value })}
                    placeholder="+1234567890"
                    required
                    data-testid="sms-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sms_message">Message *</Label>
                  <Textarea
                    id="sms_message"
                    value={smsForm.message}
                    onChange={(e) => setSmsForm({ ...smsForm, message: e.target.value })}
                    placeholder="Enter your message (max 160 characters)"
                    rows={4}
                    maxLength={160}
                    required
                    data-testid="sms-message-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {smsForm.message.length}/160 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sms_lead_id">Lead ID (optional)</Label>
                  <Input
                    id="sms_lead_id"
                    value={smsForm.lead_id}
                    onChange={(e) => setSmsForm({ ...smsForm, lead_id: e.target.value })}
                    placeholder="lead_abc123"
                    data-testid="sms-lead-id-input"
                  />
                </div>

                <Button type="submit" className="w-full gap-2" disabled={sending} data-testid="send-sms-button">
                  <Send className="h-4 w-4" />
                  {sending ? 'Sending...' : 'Send SMS'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Communications;
